import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { refreshSession } from '../api/portal';
import { useAuthStore } from '../stores/auth';

const IDLE_MS = 60 * 60_000;
const REFRESH_LEAD_MS = 10 * 60_000;
const CHECK_MS = 30_000;
const BUMP_MS = 15_000;
const IDLE_KEY = 'dp-client-idle';

function tokenExpiryMs(token: string, storedIso?: string | null): number {
  if (storedIso) {
    const t = Date.parse(storedIso);
    if (!Number.isNaN(t)) return t;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { exp?: number };
    if (typeof payload.exp === 'number') return payload.exp * 1000;
  } catch {
    /* ignore */
  }
  return 0;
}

function readIdleAt(): number {
  try {
    return Number(localStorage.getItem(IDLE_KEY) ?? Date.now());
  } catch {
    return Date.now();
  }
}

function writeIdleAt(at: number) {
  try {
    localStorage.setItem(IDLE_KEY, String(at));
  } catch {
    /* ignore */
  }
}

/** Idle-logout after 1h without activity; refresh JWT while the user is still working. */
export function useClientIdleSession() {
  const navigate = useNavigate();

  useEffect(() => {
    const signOut = () => {
      useAuthStore.getState().logout();
      navigate('/login');
    };

    let lastBump = 0;
    let refreshing = false;

    const idleOut = () => Date.now() - readIdleAt() > IDLE_MS;

    const maybeRefresh = async () => {
      const { accessToken, expiresAt, user, setSession } = useAuthStore.getState();
      if (!accessToken || !user || refreshing) return;
      const exp = tokenExpiryMs(accessToken, expiresAt);
      const remain = exp - Date.now();
      const need = accessToken === 'cookie' || exp <= 0 || remain <= REFRESH_LEAD_MS;
      if (!need) return;
      refreshing = true;
      try {
        const r = await refreshSession();
        setSession(r.access_token, r.expires_at, user);
      } catch {
        /* 401 handler already clears the session */
      } finally {
        refreshing = false;
      }
    };

    const bump = () => {
      if (idleOut()) {
        signOut();
        return;
      }
      const now = Date.now();
      if (now - lastBump < BUMP_MS) return;
      lastBump = now;
      writeIdleAt(now);
      void maybeRefresh();
    };

    const { expiresAt, accessToken } = useAuthStore.getState();
    const freshLogin = tokenExpiryMs(accessToken ?? '', expiresAt) - Date.now() > 50 * 60_000;
    if (idleOut() && !freshLogin) {
      signOut();
      return;
    }
    writeIdleAt(Date.now());
    const evts = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;
    evts.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const t = window.setInterval(() => {
      if (idleOut()) {
        signOut();
        return;
      }
      void maybeRefresh();
    }, CHECK_MS);
    void maybeRefresh();

    return () => {
      evts.forEach((e) => window.removeEventListener(e, bump));
      window.clearInterval(t);
    };
  }, [navigate]);
}
